import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { AccountAuthService, AuthError, PasswordChangeRequired, SessionPayload } from '../account-auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['../auth-form.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  readonly form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });

  busy = false;
  error: AuthError | null = null;
  /** Shown above the form (e.g. "Password changed, sign in"). */
  notice: string | null = null;
  resendState: 'idle' | 'sending' | 'sent' = 'idle';

  constructor(
    private fb: FormBuilder,
    private auth: AccountAuthService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const email = params.get('email');
    if (email) this.form.patchValue({ email });
    this.auth.rememberReturnTo(params.get('returnTo'));
    if (params.get('reason') === 'expired') this.notice = 'Your session ended. Please sign in again.';
    if (params.get('reason') === 'verified') this.notice = 'Your email is confirmed. Sign in to continue.';
  }

  get emailInvalid(): boolean {
    const c = this.form.controls.email;
    return c.invalid && c.touched;
  }

  submit(): void {
    if (this.busy) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { email, password } = this.form.getRawValue();
    this.busy = true;
    this.error = null;
    this.notice = null;
    this.resendState = 'idle';

    this.auth.login(email!.trim(), password!).subscribe({
      next: (res) => {
        if ((res as PasswordChangeRequired).password_change_required) {
          this.auth.pendingChange = { ...(res as PasswordChangeRequired), currentPassword: password! };
          this.router.navigate(['/set-new-password']);
          return;
        }
        this.auth.startSession(res as SessionPayload).catch(() => {
          this.busy = false;
          this.error = { code: 'server_error', message: 'Something went wrong on our side. Please try again in a moment.' };
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.busy = false;
        this.error = this.auth.toError(err);
        if (this.error.code === 'invalid_credentials') {
          this.form.controls.password.reset('');
        }
        this.cdr.detectChanges();
      },
    });
  }

  resendLink(): void {
    const email = this.error?.email || this.form.controls.email.value;
    if (!email || this.resendState !== 'idle') return;
    this.resendState = 'sending';
    this.auth.resendVerification(email).subscribe({
      next: () => {
        this.router.navigate(['/verify-email'], { queryParams: { email, sent: 1 } });
      },
      error: (err) => {
        this.resendState = 'idle';
        this.error = this.auth.toError(err);
        this.cdr.detectChanges();
      },
    });
  }
}
